import { gql } from '@apollo/client';

const FORM_FIELD_FRAGMENT = `
  key
  label
  type
  required
  filterable
  options
  placeholder
  order
  expectedValues
  helpText
  minFiles
  maxFiles
  captureOnly
  scan
  allowComments
  multiple
  storeScope
`;

const WORKFLOW_FIELDS = `
  # Requested so the builder round-trips it. It is part of the save
  # payload, so leaving it out of the fetch blanks it on every save.
  titleTemplate
  _id
  workflowId
  name
  description
  isActive
  icon
  color
  ticketPrefix
  ticketSeqNext
  initialStage
  form {
    ${FORM_FIELD_FRAGMENT}
  }
  standardFields {
    key
    enabled
    required
    options
    order
  }
  stages {
    key
    label
    assigneeType
    assigneeEmployeeId
    assigneeRole
    assigneeTeamId
    assignment {
      mode
      people
      byStore {
        store
        people
      }
      backups
      excludeRaiser
    }
    teamStoreScoped
    reportAccess
    bulkApprove
    bulkApproveMax
    slaHours
    slaWarnBeforeHours
    slaBumpPriority
    isTerminal
    blockSelfApproval
    allowAttachments
    form {
      ${FORM_FIELD_FRAGMENT}
    }
    notify {
      emailRequester
      emailAssignee
      pushRequester
      pushAssignee
    }
  }
  transitions {
    from
    action
    to
    label
    kind
    requiresRemark
    requiresAttachment
    actionSheet {
      confirmLabel
      confirmTone
      note {
        show
        required
        label
        placeholder
      }
      attachments {
        show
        required
        label
        min
        max
        captureOnly
      }
      violation {
        show
        required
        label
        categories
        requireCategory
        requireReason
      }
      fields {
        ${FORM_FIELD_FRAGMENT}
      }
    }
    actionForm {
      confirmLabel
      confirmTone
      note {
        show
        required
        label
        placeholder
      }
      attachments {
        show
        required
        label
        min
        max
        captureOnly
      }
      violation {
        show
        required
        label
        categories
        requireCategory
        requireReason
      }
      fields {
        ${FORM_FIELD_FRAGMENT}
      }
    }
    auto
    branches {
      to
      kind
      when {
        all {
          field
          op
          value
          values
        }
        any {
          field
          op
          value
          values
        }
      }
    }
  }
  cancel {
    enabled
    toStage
    allowRequester
    allowActor
    requireReason
  }
  visibility {
    mode
    roles
    stores
    viewerRoles
    raiserReportAccess
  }
  reportAccess
  canRaise
  canHandle
  canViewDashboard
  createdAt
  updatedAt
`;

const ATTACHMENT_FRAGMENT = `
  url
  name
  mimeType
  size
  context
  stageKey
  uploadedByEmployeeId
  at
`;

const TICKET_FIELDS = `
  _id
  ticketNumber
  title
  workflowId
  # The definition this ticket is pinned to. The server validates actions
  # against it, so the action dialog is rendered from it rather than from the
  # live (possibly since-edited) workflow.
  workflowSnapshot
  createdByEmployeeId
  participants {
    employeeId
    name
  }
  submittedData
  store
  productCategory
  requestType
  channels
  currentStage
  currentAssigneeType
  currentAssigneeEmployeeId
  currentAssigneeRole
  currentAssigneeTeamId
  currentAssigneeEmployeeIds
  priority
  slaDueAt
  dueDate
  cost
  isViolation
  violation {
    reason
    details
    category
    markedByEmployeeId
    markedAt
    violationDate
    resolvedAt
  }
  reopenCount
  resubmitCount
  lastResubmittedAt
  correctionRequest {
    requestedByEmployeeId
    at
    note
    requestedFields
    fromStage
  }
  attachments {
    ${ATTACHMENT_FRAGMENT}
  }
  history {
    action
    kind
    fromStage
    toStage
    byEmployeeId
    note
    auto
    bulk
    changes {
      key
      from
      to
    }
    attachments {
      ${ATTACHMENT_FRAGMENT}
    }
    at
  }
  comments {
    byEmployeeId
    text
    stageKey
    fieldKey
    attachments {
      ${ATTACHMENT_FRAGMENT}
    }
    at
  }
  createdAt
  updatedAt
`;

// What a list row needs. The heavy parts of a ticket — its pinned workflow
// definition, comments, attachments and the full history — load only when the
// ticket itself is opened (GET_TICKET); history here is just enough to tell
// when the current holder received it.
const TICKET_LIST_FIELDS = `
  _id
  ticketNumber
  title
  workflowId
  createdByEmployeeId
  participants {
    employeeId
    name
  }
  submittedData
  store
  productCategory
  requestType
  channels
  currentStage
  currentAssigneeType
  currentAssigneeEmployeeId
  currentAssigneeRole
  currentAssigneeTeamId
  currentAssigneeEmployeeIds
  priority
  slaDueAt
  dueDate
  cost
  isViolation
  violation {
    reason
    details
    category
    markedByEmployeeId
    markedAt
    violationDate
    resolvedAt
  }
  reopenCount
  resubmitCount
  lastResubmittedAt
  history {
    action
    toStage
    at
  }
  createdAt
  updatedAt
`;

// ==============================|| WORKFLOWS ||============================== //

export const GET_WORKFLOWS = gql`
  query getWorkflows($activeOnly: Boolean) {
    getWorkflows(activeOnly: $activeOnly) {
      ${WORKFLOW_FIELDS}
    }
  }
`;

// Just enough to list workflows (menu, pickers, filters) — not each one's full
// definition.
export const GET_WORKFLOW_LIST = gql`
  query getWorkflowList($activeOnly: Boolean) {
    getWorkflows(activeOnly: $activeOnly) {
      _id
      workflowId
      name
      isActive
      canRaise
      canViewDashboard
    }
  }
`;

export const GET_WORKFLOW = gql`
  query getWorkflow($workflowId: String!) {
    getWorkflow(workflowId: $workflowId) {
      ${WORKFLOW_FIELDS}
    }
  }
`;

const GROUP_ROW_FRAGMENT = `
  key
  label
  total
  open
  completed
  rejected
  cancelled
  slaBreached
  violations
`;

export const GET_WORKFLOW_DASHBOARD = gql`
  query getWorkflowDashboard(
    $workflowId: String
    $trendDays: Float
    $store: String
    $priority: String
    $country: String
    $from: DateTime
    $to: DateTime
  ) {
    getWorkflowDashboard(
      workflowId: $workflowId
      trendDays: $trendDays
      store: $store
      priority: $priority
      country: $country
      from: $from
      to: $to
    ) {
      workflowId
      workflowName
      total
      open
      completed
      rejected
      cancelled
      slaBreached
      slaAtRisk
      attentionTotal
      attentionOverdue
      violations
      byStore {
        ${GROUP_ROW_FRAGMENT}
      }
      byStage {
        ${GROUP_ROW_FRAGMENT}
      }
      byPriority {
        ${GROUP_ROW_FRAGMENT}
      }
      byWorkflow {
        ${GROUP_ROW_FRAGMENT}
      }
      attention {
        ticketNumber
        title
        workflowId
        workflowName
        stageLabel
        priority
        store
        slaBreached
        slaDueAt
        ageDays
        createdAt
        raisedByName
      }
      taskQueue {
        ticketNumber
        title
        workflowId
        workflowName
        stageLabel
        priority
        store
        slaBreached
        slaDueAt
        ageDays
        createdAt
        raisedByName
      }
      heatmap {
        columns {
          workflowId
          workflowName
        }
        rows {
          store
          total
          cells {
            workflowId
            total
            open
            overdue
            completed
          }
        }
      }
      recentActivity {
        ticketNumber
        workflowName
        action
        stageLabel
        byName
        priority
        at
      }
      createdTrend {
        date
        count
      }
      generatedAt
    }
  }
`;

// Tickets behind one dashboard number (a breakdown row, or Needs attention),
// under the same filters as the dashboard.
export const GET_WORKFLOW_DASHBOARD_TICKETS = gql`
  query getWorkflowDashboardTickets(
    $workflowId: String
    $groupBy: String!
    $groupKey: String!
    $status: String
    $store: String
    $priority: String
    $country: String
    $from: DateTime
    $to: DateTime
    $page: Int
    $limit: Int
  ) {
    getWorkflowDashboardTickets(
      workflowId: $workflowId
      groupBy: $groupBy
      groupKey: $groupKey
      status: $status
      store: $store
      priority: $priority
      country: $country
      from: $from
      to: $to
      page: $page
      limit: $limit
    ) {
      items {
        ${TICKET_LIST_FIELDS}
      }
      pageInfo {
        currentPage
        totalPages
        totalItems
      }
      counts {
        total
        open
        completed
        rejected
        cancelled
        slaBreached
        urgent
      }
    }
  }
`;

export const CREATE_WORKFLOW = gql`
  mutation createWorkflow($input: CreateWorkflowInput!) {
    createWorkflow(input: $input) {
      ${WORKFLOW_FIELDS}
    }
  }
`;

export const UPDATE_WORKFLOW = gql`
  mutation updateWorkflow($input: UpdateWorkflowInput!) {
    updateWorkflow(input: $input) {
      ${WORKFLOW_FIELDS}
    }
  }
`;

// ==============================|| TICKETS ||============================== //

export const GET_TICKETS = gql`
  query getTickets($filter: ListTicketsInput) {
    getTickets(filter: $filter) {
      ${TICKET_FIELDS}
    }
  }
`;

export const BULK_APPROVE_TICKETS = gql`
  mutation bulkApproveTickets($input: BulkApproveInput!) {
    bulkApproveTickets(input: $input) {
      approved
      failed
      results {
        ticketId
        ticketNumber
        ok
        error
      }
    }
  }
`;

export const GET_TICKETS_PAGE = gql`
  query getTicketsPage($filter: ListTicketsInput) {
    getTicketsPage(filter: $filter) {
      items {
        ${TICKET_LIST_FIELDS}
      }
      pageInfo {
        currentPage
        totalPages
        currentItem
        totalItems
      }
      counts {
        total
        open
        completed
        rejected
        cancelled
        slaBreached
        dueToday
        urgent
      }
    }
  }
`;

// Tickets the viewer could approve in bulk: list fields plus the approval
// steps pinned on each ticket (not its whole workflow definition).
export const GET_BULK_CANDIDATES = gql`
  query getBulkCandidates($filter: ListTicketsInput) {
    getTicketsPage(filter: $filter) {
      items {
        ${TICKET_LIST_FIELDS}
        pinnedTransitions
      }
    }
  }
`;

// How many tickets match each filter (the scope tabs' badges) in one request;
// a filter with a limit also returns that many matching ids.
export const GET_TICKET_COUNTS = gql`
  query getTicketCounts($filters: [ListTicketsInput!]!) {
    getTicketCounts(filters: $filters) {
      total
      ids
    }
  }
`;

export const GET_TICKET = gql`
  query getTicket($ticketId: String!) {
    getTicket(ticketId: $ticketId) {
      ${TICKET_FIELDS}
      canAct
    }
  }
`;

export const CREATE_TICKET = gql`
  mutation createTicket($input: CreateTicketInput!) {
    createTicket(input: $input) {
      ${TICKET_FIELDS}
    }
  }
`;

export const TRANSITION_TICKET = gql`
  mutation transitionTicket($input: TransitionTicketInput!) {
    transitionTicket(input: $input) {
      ${TICKET_FIELDS}
    }
  }
`;

export const REASSIGN_TICKET = gql`
  mutation reassignTicket($input: ReassignTicketInput!) {
    reassignTicket(input: $input) {
      ${TICKET_FIELDS}
    }
  }
`;

export const ADD_COMMENT = gql`
  mutation addComment($input: AddCommentInput!) {
    addComment(input: $input) {
      ${TICKET_FIELDS}
    }
  }
`;

export const RESOLVE_VIOLATION = gql`
  mutation resolveViolation($ticketId: String!) {
    resolveViolation(ticketId: $ticketId) {
      ${TICKET_FIELDS}
    }
  }
`;

// Damage Declaration export (Taqtics layout). Returns the .xlsx inline (base64).
export const DAMAGE_DECLARATION_REPORT = gql`
  query damageDeclarationReport($input: DamageDeclarationReportInput!) {
    damageDeclarationReport(input: $input) {
      fileName
      mimeType
      contentBase64
      rowCount
    }
  }
`;
